import React, { useState, useEffect } from 'react';
import { getStockHistory, StockLog, BranchStockItem } from '../../services/stockService';
import { X, Clock, TrendingUp, TrendingDown, Package, AlertCircle, RotateCcw } from 'lucide-react';

interface StockHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: BranchStockItem;
  branchId: number;
}

const StockHistoryModal: React.FC<StockHistoryModalProps> = ({
  isOpen,
  onClose,
  item,
  branchId,
}) => {
  const [history, setHistory] = useState<StockLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [limit, setLimit] = useState(50);

  useEffect(() => {
    if (isOpen) {
      loadHistory();
    }
  }, [isOpen, limit]);

  const loadHistory = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getStockHistory(item.item_id, branchId, limit);
      setHistory(data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load stock history');
    } finally {
      setLoading(false);
    }
  };

  const getRefTypeLabel = (refType: string): string => {
    const labels: Record<string, string> = {
      sale: 'Penjualan',
      mutation_out: 'Mutasi Keluar',
      mutation_in: 'Mutasi Masuk',
      restock: 'Restok',
      adjustment: 'Penyesuaian',
      return: 'Retur Customer',
      void: 'Void/Batal',
      po_receive: 'Terima PO',
      supplier_return: 'Retur Supplier',
    };
    return labels[refType] || refType;
  };

  const getRefTypeIcon = (refType: string) => {
    const isIncrease = ['mutation_in', 'restock', 'return', 'void', 'po_receive'].includes(refType);
    const isDecrease = ['sale', 'mutation_out', 'supplier_return'].includes(refType);
    
    if (isIncrease) return <TrendingUp className="w-5 h-5 text-green-600" />;
    if (isDecrease) return <TrendingDown className="w-5 h-5 text-red-600" />;
    return <Package className="w-5 h-5 text-blue-600" />;
  };

  const getRefTypeColor = (refType: string): string => {
    const isIncrease = ['mutation_in', 'restock', 'return', 'void', 'po_receive'].includes(refType);
    const isDecrease = ['sale', 'mutation_out', 'supplier_return'].includes(refType);
    
    if (isIncrease) return 'bg-green-100 text-green-800 border-green-200';
    if (isDecrease) return 'bg-red-100 text-red-800 border-red-200';
    return 'bg-blue-100 text-blue-800 border-blue-200';
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString('id-ID', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Stock Movement History</h2>
            <p className="text-sm text-gray-600 mt-1">{item.name} ({item.sku})</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Current Stock Info */}
        <div className="p-6 bg-gray-50 border-b border-gray-200">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <div className="text-sm text-gray-600">Current Stock</div>
              <div className="text-2xl font-bold text-gray-900">
                {item.qty} {item.unit}
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-600">Minimum Stock</div>
              <div className="text-lg font-semibold text-gray-700">
                {item.min_stock} {item.unit}
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-600">Status</div>
              {item.qty === 0 ? (
                <span className="inline-block px-3 py-1 text-sm font-semibold rounded-full bg-red-100 text-red-800">
                  Out of Stock
                </span>
              ) : item.is_low_stock ? (
                <span className="inline-block px-3 py-1 text-sm font-semibold rounded-full bg-orange-100 text-orange-800">
                  Low Stock
                </span>
              ) : (
                <span className="inline-block px-3 py-1 text-sm font-semibold rounded-full bg-green-100 text-green-800">
                  Available
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              <p className="mt-4 text-gray-600">Loading history...</p>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
              <p className="text-red-600 mb-4">{error}</p>
              <button
                onClick={loadHistory}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Retry
              </button>
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-12">
              <Clock className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600">No history found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {history.map((log) => (
                <div
                  key={log.id}
                  className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3 flex-1">
                      {/* Icon */}
                      <div className="mt-1">
                        {getRefTypeIcon(log.ref_type)}
                      </div>

                      {/* Content */}
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <span className={`px-2 py-1 text-xs font-semibold rounded border ${getRefTypeColor(log.ref_type)}`}>
                            {getRefTypeLabel(log.ref_type)}
                          </span>
                          {log.ref_id && (
                            <span className="text-xs text-gray-500">
                              Ref: #{log.ref_id}
                            </span>
                          )}
                        </div>

                        <div className="text-sm text-gray-600 mb-2">
                          <Clock className="w-4 h-4 inline mr-1" />
                          {formatDate(log.created_at)}
                        </div>

                        {log.notes && (
                          <div className="text-sm text-gray-700 bg-gray-50 p-2 rounded">
                            {log.notes}
                          </div>
                        )}
                      </div>

                      {/* Stock Changes */}
                      <div className="text-right min-w-[200px]">
                        <div className="text-xs text-gray-500 mb-1">Stock Changes</div>
                        <div className="flex items-center justify-end space-x-2 text-sm">
                          <span className="font-semibold text-gray-700">
                            {log.qty_before}
                          </span>
                          <span className="text-gray-400">→</span>
                          <span className={`font-bold ${
                            log.qty_change > 0 ? 'text-green-600' :
                            log.qty_change < 0 ? 'text-red-600' :
                            'text-gray-600'
                          }`}>
                            {log.qty_change > 0 ? '+' : ''}{log.qty_change}
                          </span>
                          <span className="text-gray-400">→</span>
                          <span className="font-semibold text-gray-900">
                            {log.qty_after}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200">
          <div className="text-sm text-gray-600">
            Showing {history.length} of latest records
          </div>
          <div className="flex items-center space-x-3">
            <select
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value={25}>25 records</option>
              <option value={50}>50 records</option>
              <option value={100}>100 records</option>
              <option value={200}>200 records</option>
            </select>
            <button
              onClick={loadHistory}
              disabled={loading}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 flex items-center"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Refresh
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StockHistoryModal;
